import { useMemo } from "react";
import { RemovableChip } from "@/components/removable-chip";
import {
  SearchMultiSelect,
  type AllSelectorConfig,
  type MultiSelectValueProps,
  type SelectItem,
} from "@/components/search-multi-select";
import type { ReactNode } from "react";

type SearchMultiSelectWithTagsProps = MultiSelectValueProps & {
  items: SelectItem[];
  placeholder?: string;
  icon?: ReactNode;
  name?: string;
  allSelector?: AllSelectorConfig;
  disabled?: boolean;
};

export function SearchMultiSelectWithTags({
  items,
  allSelector,
  disabled = false,
  ...rest
}: SearchMultiSelectWithTagsProps) {
  const { selected, nullSelector } = rest;
  const selectedValues = useMemo(() => selected ?? [], [selected]);
  const selectedItems = useMemo(
    () => items.filter((i) => selectedValues.includes(i.value)),
    [items, selectedValues],
  );
  // Safe: `null` is only ever emitted when `nullSelector` is set.
  const onChange = rest.onChange as (next: string[] | null) => void;

  const showNullChip = !!nullSelector && selected === null;
  const showAllChip = !!allSelector && selected !== null && selected.length === 0;

  return (
    <div className="flex w-full flex-col gap-1">
      <SearchMultiSelect items={items} allSelector={allSelector} disabled={disabled} {...rest} />
      {(showNullChip || showAllChip || selectedItems.length > 0) && (
        <div className="flex w-full flex-wrap items-start gap-1">
          {showNullChip && (
            <RemovableChip icon={nullSelector.icon} removeLabel={nullSelector.name ?? "None"}>
              {nullSelector.name ?? "None"}
            </RemovableChip>
          )}
          {showAllChip && (
            <RemovableChip icon={allSelector.icon} removeLabel={allSelector.name ?? "All"}>
              {allSelector.name ?? "All"}
            </RemovableChip>
          )}
          {selectedItems.map((item) => (
            <RemovableChip
              key={item.value}
              className={[
                "inline-flex items-center gap-1 rounded-sm px-2 py-1 text-small font-medium",
                disabled ? "bg-grey-100 text-grey-500" : "bg-grey-950 text-white",
              ].join(" ")}
              icon={item.icon}
              removeLabel={item.name}
              onRemove={
                disabled
                  ? undefined
                  : () => onChange(selectedValues.filter((v) => v !== item.value))
              }
            >
              {item.name}
            </RemovableChip>
          ))}
        </div>
      )}
    </div>
  );
}
